import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import consultantsImage from '../assets/images/Image 4_Consultants.png';
import SEO from '../components/SEO';
import { useTrack } from '../contexts/TrackContext';

const styles = `
  .feature-card {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    cursor: pointer;
    transform-style: preserve-3d;
    background: linear-gradient(165deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%);
    opacity: 0;
    transform: translateY(20px);
    animation: fadeInUp 0.5s forwards;
    border: 1px solid rgba(59, 130, 246, 0.1);
  }

  .feature-card:hover {
    transform: translateY(-4px) scale(1.02);
    box-shadow: 0 12px 24px -10px rgba(66, 153, 225, 0.3);
    border-color: rgba(59, 130, 246, 0.2);
    background: linear-gradient(165deg, rgba(255, 255, 255, 0.98) 0%, rgba(239, 246, 255, 0.9) 100%);
  }

  .feature-card.pulse-glow {
    animation: fadeInUp 0.5s forwards, pulseGlow 2s infinite;
    border: 1px solid rgba(59, 130, 246, 0.3);
    box-shadow: 0 0 15px rgba(59, 130, 246, 0.2);
  }

  .feature-card.pulse-glow:hover {
    box-shadow: 0 0 25px rgba(59, 130, 246, 0.4);
    border-color: rgba(59, 130, 246, 0.5);
  }

  .feature-icon {
    transition: all 0.3s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 50px;
    height: 50px;
    background: linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%);
    border-radius: 12px;
    margin-bottom: 1rem;
    font-size: 2rem;
  }

  .feature-card:hover .feature-icon {
    transform: scale(1.1) rotate(-5deg);
    background: linear-gradient(135deg, #DBEAFE 0%, #BFDBFE 100%);
  }

  .section-title {
    background: linear-gradient(135deg, #000 0%, #333 25%, #000 50%, #666 75%, #000 100%);
    -webkit-background-clip: text;
    color: transparent;
    position: relative;
    display: inline-block;
    text-shadow: 0 1px 1px rgba(255, 255, 255, 0.1);
  }

  .section-title::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background: linear-gradient(to right, transparent 0%, rgba(0, 0, 0, 0.3) 25%, rgba(0, 0, 0, 0.5) 50%, rgba(0, 0, 0, 0.3) 75%, transparent 100%);
    border-radius: 2px;
  }

  @keyframes fadeInUp {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes pulseGlow {
    0% {
      box-shadow: 0 0 15px rgba(59, 130, 246, 0.2);
      border-color: rgba(59, 130, 246, 0.3);
    }
    50% {
      box-shadow: 0 0 25px rgba(59, 130, 246, 0.4);
      border-color: rgba(59, 130, 246, 0.5);
    }
    100% {
      box-shadow: 0 0 15px rgba(59, 130, 246, 0.2);
      border-color: rgba(59, 130, 246, 0.3);
    }
  }

  .learn-more-text {
    color: #3B82F6;
    font-weight: 500;
    margin-top: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    transition: all 0.3s ease;
  }

  .feature-card:hover .learn-more-text {
    transform: translateX(4px);
    color: #2563EB;
  }

  .learn-more-text::after {
    content: '→';
    transition: transform 0.3s ease;
  }

  .feature-card:hover .learn-more-text::after {
    transform: translateX(4px);
  }
`;

const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

const sectionFade = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -16 },
  transition: { duration: 0.35 },
};

export default function TryForFreePage() {
  const navigate = useNavigate();
  const { track, setTrack } = useTrack();
  const isMck = track === 'mck' || track === 'all';

  return (
    <div className="bg-white text-gray-900 font-sans">
      <SEO
        title="Try for Free – McKinsey Solve & BCG Casey Prep"
        description="Try Cognidoor free: explore McKinsey Solve simulations, BCG Casey practice, AI coaching, and quantitative drills before subscribing."
        url="/try-for-free"
      />

      {/* Hero Section */}
      <section className="relative z-30 bg-gradient-to-br from-blue-100 via-blue-50 to-white pb-12 min-h-[500px] flex items-center overflow-visible">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900/90 via-gray-800/85 to-gray-700/80">
          <div className="absolute inset-0" style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            mixBlendMode: 'overlay'
          }} />
        </div>
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="absolute z-10 hidden md:block hero-image w-[400px] aspect-[2/3]"
          style={{ right: '5vw', top: '40px' }}
        >
          <div className="h-full rounded-xl overflow-hidden shadow-2xl transform hover:scale-[1.02] transition-transform duration-500">
            <img
              src={consultantsImage}
              alt="Consultants"
              className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-700"
            />
          </div>
        </motion.div>
        <div className="container pl-[72px] grid grid-cols-1 md:grid-cols-2 gap-12 py-24 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl hero-content"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight text-white">
              Try Cognidoor for Free
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-10 leading-relaxed">
              Prepare smarter for McKinsey Solve and BCG Casey. Explore realistic simulations, AI coaching, and quantitative drills — all free to try.
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/about-solve')}
                className="group relative px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-400 text-white text-lg rounded-xl shadow-md hover:shadow-xl transition-all duration-300"
              >
                Learn about McKinsey Solve
                <div className="absolute inset-0 bg-white rounded-xl opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/about-bcg-casey')}
                className="group relative px-8 py-4 border-2 border-white/80 text-white text-lg rounded-xl hover:bg-white/10 transition-all duration-300"
              >
                Learn about BCG Casey
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Track Switcher */}
      <section className="py-8 bg-white text-center">
        <p className="text-sm text-gray-500 mb-3">Which assessment are you preparing for?</p>
        <div className="inline-flex border rounded-2xl p-1 border-slate-200 bg-slate-50">
          <button
            onClick={() => setTrack('mck')}
            className={`px-5 py-2.5 text-sm font-medium transition-all duration-300 rounded-xl ${
              isMck
                ? 'text-white bg-gradient-to-r from-blue-600 to-blue-400 shadow-md'
                : 'text-slate-600 hover:bg-white/60'
            }`}
          >
            McKinsey Solve
          </button>
          <button
            onClick={() => setTrack('bcg')}
            className={`px-5 py-2.5 text-sm font-medium transition-all duration-300 rounded-xl ${
              track === 'bcg'
                ? 'text-white bg-gradient-to-r from-blue-600 to-blue-400 shadow-md'
                : 'text-slate-600 hover:bg-white/60'
            }`}
          >
            BCG Casey
          </button>
        </div>
      </section>

      {/* Track-specific content */}
      <AnimatePresence mode="wait">
        {isMck ? (
          <motion.div key="mck" {...sectionFade}>
            {/* McKinsey Solve Section */}
            <section className="py-16 bg-gradient-to-b from-white via-blue-50/30 to-white text-center px-6 relative">
              <h2 className="text-3xl sm:text-4xl font-bold mb-3 section-title tracking-tight">McKinsey Solve Assessment</h2>
              <p className="text-gray-500 mt-6 mb-12 max-w-2xl mx-auto">A ~65-minute digital assessment with two game-based challenges. Around 80% of candidates are eliminated at this stage — preparation matters.</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate('/redrock#explore')}
                  className="feature-card rounded-2xl shadow-md p-8 backdrop-blur-sm cursor-pointer flex flex-col h-full"
                >
                  <div className="feature-icon">🏔️</div>
                  <h3 className="text-lg font-semibold mb-3 text-gray-800">Redrock Study</h3>
                  <p className="text-sm text-gray-600 leading-relaxed flex-grow">Analyze ecological data as a researcher on Redrock Island. Work through 12+ exercises covering calculations, formula interpretation, and chart analysis — the core of McKinsey's quantitative evaluation.</p>
                  <p className="text-sm text-blue-600 font-medium mt-4">Try two free Red Rock cases and experience realistic practice scenarios.</p>
                  <div className="learn-more-text mt-4">Try Now</div>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate('/seawolf')}
                  className="feature-card rounded-2xl shadow-md p-8 backdrop-blur-sm cursor-pointer flex flex-col h-full"
                >
                  <div className="feature-icon">🌊</div>
                  <h3 className="text-lg font-semibold mb-3 text-gray-800">Sea Wolf Game</h3>
                  <p className="text-sm text-gray-600 leading-relaxed flex-grow">Clean up contaminated ocean sites by selecting microbe teams that match attribute ranges and trait requirements. Tests your ability to systematically evaluate options and optimize under constraints.</p>
                  <p className="text-sm text-blue-600 font-medium mt-4">Practice with our interactive Sea Wolf guide and solver tools.</p>
                  <div className="learn-more-text mt-4">Try Now</div>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate('/ecosystem-solver')}
                  className="feature-card rounded-2xl shadow-md p-8 backdrop-blur-sm cursor-pointer flex flex-col h-full"
                >
                  <div className="feature-icon">🌱</div>
                  <h3 className="text-lg font-semibold mb-3 text-gray-800">Ecosystem Building</h3>
                  <p className="text-sm text-gray-600 leading-relaxed flex-grow">Build sustainable food chains by selecting species based on calorie needs and interdependencies. Being phased out globally, but still appears for some candidates with 110-minute invites.</p>
                  <p className="text-sm text-blue-600 font-medium mt-4">Try our solver — pick a dataset and get instant results.</p>
                  <div className="learn-more-text mt-4">Try Now</div>
                </motion.div>
              </div>
            </section>
          </motion.div>
        ) : (
          <motion.div key="bcg" {...sectionFade}>
            {/* BCG Casey Section */}
            <section className="py-16 bg-gradient-to-b from-white via-blue-50/30 to-white text-center px-6 relative">
              <h2 className="text-3xl sm:text-4xl font-bold mb-3 section-title tracking-tight">BCG Casey Assessment</h2>
              <p className="text-gray-500 mt-6 mb-12 max-w-2xl mx-auto">A 35-minute chatbot-based case study plus a 1-minute video recommendation. 70–80% of candidates are screened out — structured practice gives you the edge.</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate('/about-bcg-casey')}
                  className="feature-card rounded-2xl shadow-md p-8 backdrop-blur-sm cursor-pointer flex flex-col h-full"
                >
                  <div className="feature-icon">💬</div>
                  <h3 className="text-lg font-semibold mb-3 text-gray-800">Casey Chatbot Case</h3>
                  <p className="text-sm text-gray-600 leading-relaxed flex-grow">Work through 8–10 timed questions in a chatbot format covering market sizing, financial analysis, chart interpretation, and structured problem-solving. No pausing or going back once you start.</p>
                  <p className="text-sm text-blue-600 font-medium mt-4">Learn what to expect and how to prepare effectively.</p>
                  <div className="learn-more-text mt-4">Learn More</div>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate('/bcg-ai-coach')}
                  className="feature-card rounded-2xl shadow-md p-8 backdrop-blur-sm cursor-pointer flex flex-col h-full"
                >
                  <div className="feature-icon">🎯</div>
                  <h3 className="text-lg font-semibold mb-3 text-gray-800">AI Case Coach</h3>
                  <p className="text-sm text-gray-600 leading-relaxed flex-grow">Practice candidate-led case interviews with our AI coach. Get real-time feedback on your structuring, quantitative reasoning, and communication — the key skills BCG evaluates.</p>
                  <p className="text-sm text-blue-600 font-medium mt-4">Sharpen your case interview technique with AI feedback.</p>
                  <div className="learn-more-text mt-4">Try Now</div>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate('/bcg-casey-simulation')}
                  className="feature-card rounded-2xl shadow-md p-8 backdrop-blur-sm cursor-pointer flex flex-col h-full"
                >
                  <div className="feature-icon">📋</div>
                  <h3 className="text-lg font-semibold mb-3 text-gray-800">Casey Simulation</h3>
                  <p className="text-sm text-gray-600 leading-relaxed flex-grow">Run a full timed Casey simulation with realistic case scenarios, exhibit analysis, and quantitative questions. Experience the actual format and time pressure before test day.</p>
                  <p className="text-sm text-blue-600 font-medium mt-4">Simulate the real assessment experience end to end.</p>
                  <div className="learn-more-text mt-4">Try Now</div>
                </motion.div>
              </div>
            </section>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Shared: Quantitative Practice */}
      <section className="py-16 bg-gradient-to-b from-white via-blue-50/20 to-white px-6 text-center relative">
        <h2 className="text-3xl sm:text-4xl font-bold mb-3 section-title tracking-tight">Practice Quantitative Skills</h2>
        <p className="text-gray-500 mt-6 mb-12 max-w-2xl mx-auto">Both McKinsey Solve and BCG Casey test your math and chart interpretation skills heavily. Build your speed and accuracy with free practice.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          <motion.div
            className="feature-card rounded-2xl shadow-md p-8 backdrop-blur-sm cursor-pointer pulse-glow"
            style={{ animationDelay: '0.1s' }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/consulting-math-guides#math-section')}
          >
            <div className="feature-icon">📐</div>
            <h3 className="text-lg font-semibold mb-3 text-gray-800">Math Practice</h3>
            <p className="text-sm text-gray-600 leading-relaxed">Percentages, growth rates, market sizing, and multi-step calculations. Practice the exact question types you'll face in both assessments with an interactive calculator.</p>
            <div className="learn-more-text">Start Practicing</div>
          </motion.div>
          <motion.div
            className="feature-card rounded-2xl shadow-md p-8 backdrop-blur-sm cursor-pointer pulse-glow"
            style={{ animationDelay: '0.2s' }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/consulting-math-guides#chart-section')}
          >
            <div className="feature-icon">📊</div>
            <h3 className="text-lg font-semibold mb-3 text-gray-800">Chart Interpretation</h3>
            <p className="text-sm text-gray-600 leading-relaxed">Bar charts, line graphs, scatter plots, and waterfall diagrams. Learn to extract insights quickly and accurately — a skill that directly impacts your score in both assessments.</p>
            <div className="learn-more-text">Start Practicing</div>
          </motion.div>
        </div>
      </section>

      {/* Why Cognidoor */}
      <section className="py-16 bg-gradient-to-b from-white via-slate-50/50 to-white px-6 text-center relative">
        <h2 className="text-3xl sm:text-4xl font-bold mb-3 section-title tracking-tight">Why Prepare with Cognidoor</h2>
        <p className="text-gray-500 mt-6 mb-12 max-w-2xl mx-auto">Purpose-built tools for McKinsey and BCG assessment prep — not generic case practice.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
          <div className="feature-card rounded-2xl shadow-md p-8 backdrop-blur-sm flex flex-col h-full" style={{ cursor: 'default' }}>
            <div className="feature-icon">🎮</div>
            <h3 className="text-lg font-semibold mb-3 text-gray-800">Realistic Simulations</h3>
            <p className="text-sm text-gray-600 leading-relaxed flex-grow">Practice in environments that mirror the actual assessments — from timed chatbot cases to interactive game-based challenges. No surprises on test day.</p>
          </div>
          <div className="feature-card rounded-2xl shadow-md p-8 backdrop-blur-sm flex flex-col h-full" style={{ cursor: 'default' }}>
            <div className="feature-icon">🤖</div>
            <h3 className="text-lg font-semibold mb-3 text-gray-800">AI-Powered Coaching</h3>
            <p className="text-sm text-gray-600 leading-relaxed flex-grow">Get personalized feedback from AI coaches that analyze your approach, not just your answers. Understand where you're losing points and how to improve.</p>
          </div>
          <div className="feature-card rounded-2xl shadow-md p-8 backdrop-blur-sm flex flex-col h-full" style={{ cursor: 'default' }}>
            <div className="feature-icon">🆓</div>
            <h3 className="text-lg font-semibold mb-3 text-gray-800">Free to Explore</h3>
            <p className="text-sm text-gray-600 leading-relaxed flex-grow">Try Red Rock drills, ecosystem solver demos, and math practice at no cost. See the quality for yourself, then upgrade when you're ready for full access.</p>
          </div>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate('/', { state: { scrollToPricing: true } })}
          className="mt-12 px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-400 text-white text-lg rounded-xl shadow-md hover:shadow-xl transition-all duration-300"
        >
          View Plans & Pricing
        </motion.button>
      </section>
    </div>
  );
}
